import { Component } from '@theme/component';
import { turnstileRequired, turnstileToken as solveTurnstile } from '@theme/turnstile';

/**
 * Invite code redemption form.
 *
 * Posts to the App Proxy at /apps/invite/redeem, which forwards to our Worker
 * with Shopify's signed `logged_in_customer_id`. Nothing here is a security
 * boundary — the Worker decides, and it does not trust anything this file says.
 * The job of this component is purely to be clear about what happened.
 *
 * Progressive enhancement: the markup is a real <form> with a real submit
 * button. Without JavaScript it posts normally and the customer still gets a
 * result page; this only upgrades it to stay in place.
 */
class InviteCodeForm extends Component {
  /** @type {boolean} */
  #busy = false;

  mount() {
    this.on(this, 'submit', (event) => {
      event.preventDefault();
      this.#submit();
    });
  }

  async #submit() {
    if (this.#busy) return;

    const input = /** @type {HTMLInputElement | undefined} */ (this.refs['code']);
    const code = input?.value?.trim() ?? '';
    if (!code) {
      this.#status('Enter your invitation code.', 'error');
      input?.focus();
      return;
    }

    this.#setBusy(true);
    this.#status('Checking…', 'pending');

    // Wait for the bot-check token before sending. The widget solves
    // asynchronously, so reading it the instant the form is submitted often
    // finds it empty — which the server (correctly) rejects. Waiting here makes
    // the common case just work; only a genuinely stuck widget falls through.
    // Shared with the other three gated forms: see assets/turnstile.js.
    const turnstileToken = await solveTurnstile(this, () =>
      this.#status('Verifying you are human…', 'pending')
    );
    if (turnstileRequired(this) && !turnstileToken) {
      this.#status('We could not verify your browser. Please refresh and try again — if it keeps happening, contact your concierge.', 'error');
      this.#setBusy(false);
      return;
    }

    try {
      const response = await fetch('/apps/invite/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, turnstileToken }),
      });

      // 401 means Shopify did not send a logged-in customer. The member needs
      // an account before a code can be attached to them.
      if (response.status === 401) {
        this.#status('Please sign in to your account first, then enter your code.', 'error');
        return;
      }
      if (response.status === 429) {
        this.#status('Too many attempts. Please contact your concierge.', 'error');
        return;
      }
      // 403 = the bot check did not pass (usually no token yet, or a browser
      // extension blocked it). Tell them how to recover instead of a vague error.
      if (response.status === 403) {
        this.#status('We could not verify your browser. Please refresh and try again — if it keeps happening, contact your concierge.', 'error');
        return;
      }
      if (!response.ok) {
        this.#status('Something went wrong. Please try again shortly.', 'error');
        return;
      }

      const result = await response.json();

      if (result.ok) {
        // TAKE OVER THE PANEL. A one-line status under the field reads exactly
        // like the error state, which is the wrong signal for the one moment in
        // this funnel that has to feel certain. Hiding the form also removes any
        // chance of a second submit while the redirect is pending.
        const form = this.querySelector('form');
        const accepted = /** @type {HTMLElement | undefined} */ (this.refs['accepted']);
        if (form && accepted) {
          form.hidden = true;
          accepted.hidden = false;
          // Move focus so a screen reader lands on the outcome rather than
          // being left on a control that is now hidden.
          accepted.setAttribute('tabindex', '-1');
          accepted.focus({ preventScroll: true });
        } else {
          // Panel missing (older stored markup) — the status line still says it.
          this.#status('Your invitation has been accepted. Preparing your membership…', 'success');
        }
        this.emit('invite:redeemed');
        // Send them to the next step rather than back to the form they just
        // finished with. A full navigation, not a client-side swap, so the
        // entitlement-gated markup is rendered by the server: the page remains
        // the source of truth for what a member may see, not this JS.
        //
        // THE DELAY IS WAITING FOR SHOPIFY, NOT FOR EFFECT, and 900ms was too
        // short. The Worker writes `yuuth.membership` through the Admin API and
        // returns as soon as that write is acknowledged. The storefront reads
        // it back through Liquid — `customer.metafields.yuuth.membership.value`
        // in snippets/membership-state.liquid — and that read is EVENTUALLY
        // CONSISTENT with the Admin write. D1 is already correct; Shopify's
        // storefront view of the metafield is what lags.
        //
        // Navigating before it catches up lands the customer on the gated page
        // still rendering the gate: they see "membership is by invitation" one
        // second after being told their invitation was accepted, which reads as
        // a failure rather than a delay. The redeem itself succeeded, so a
        // manual refresh fixes it — but nobody refreshes a page that just told
        // them no.
        //
        // A fixed delay cannot be correct for a variable latency, so the
        // destination carries `yuuth_redeemed=1` plus `yuuth_state` and
        // re-checks on arrival — see assets/membership-settle.js. This timeout
        // only covers the common case so the usual path needs no retry at all.
        //
        // That reference said assets/invite-handoff.js until 2026-08-21, which
        // is a file that no longer does anything and is loaded by nothing. An
        // adversarial reviewer followed it, found only the dead stash-and-resume
        // code, and reported that the retry logic had never been written. A
        // comment pointing at the wrong file does not just fail to help; it
        // actively produces false conclusions.
        // WHICH DESTINATION DEPENDS ON WHAT THE CODE GRANTED, and until
        // 2026-08-31 it did not: every redemption went to successUrl, which
        // defaults to /pages/welcome, the intake form. That is right for
        // eligible, the ordinary case, and wrong for member -- /pages/welcome
        // exists to collect what a member has already given, so it answers a
        // successful redemption with "your profile is already complete".
        //
        // `state` is already on the response and already trusted: it is what
        // membership-settle.js waits for below, so using it here means the
        // destination and the settle check cannot disagree.
        // ONLY `member` BRANCHES, and an applicant branch was tried here and
        // removed before it shipped. Sending the applicant demo grant to
        // /pages/apply looked right -- that page has the real status panel --
        // but the comment in blocks/invite-code.liquid records why that page was
        // rejected as a redemption destination in the first place: on arrival
        // the metafield has not settled, so the page renders its pre-approval
        // state and membership-settle.js then reloads underneath it. For an
        // applicant that pre-approval state is the full, SUBMITTABLE application
        // form, offered seconds after being told their invitation was accepted.
        // /pages/welcome is wrong for that beat too, but it is wrong as a static
        // panel rather than as a live form, so it stays.
        //
        // Explicit condition rather than a lookup object: `result.state` comes
        // off the wire and `{}[someString]` finds inherited keys, so
        // 'constructor' would hand a function to new URL(). Unreachable given
        // what the server sends, but this is the redemption path.
        const stateUrl = result.state === 'member' ? this.dataset.memberUrl : '';
        // Falls through to successUrl for eligible, for applicant, for an
        // unknown state, and whenever memberUrl is blank -- so every path that
        // is not the one deliberate branch keeps the behaviour it had.
        const next = stateUrl || this.dataset.successUrl || '';
        setTimeout(() => {
          if (next) {
            const url = new URL(next, window.location.origin);
            url.searchParams.set('yuuth_redeemed', '1');
            // The state the server says this code grants, so the destination
            // waits for THAT state rather than for any signed-in state. A member
            // code redeemed by an account already showing `eligible` would
            // otherwise look settled on arrival and stop, leaving the person one
            // state short with no indication anything was wrong.
            if (result.state) url.searchParams.set('yuuth_state', String(result.state));
            window.location.assign(url.toString());
          } else {
            window.location.reload();
          }
        }, 2200);
        return;
      }

      // The server returns one message for every failure mode on purpose, so
      // this endpoint cannot be used to discover whether a code exists.
      this.#status('That invitation code is not valid.', 'error');
    } catch {
      this.#status('Could not reach the server. Please try again.', 'error');
    } finally {
      this.#setBusy(false);
    }
  }

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.#busy = busy;
    const button = /** @type {HTMLButtonElement | undefined} */ (this.refs['submit']);
    if (button) button.disabled = busy;
    this.toggleAttribute('data-busy', busy);
  }

  /**
   * @param {string} message
   * @param {'pending' | 'error' | 'success'} tone
   */
  #status(message, tone) {
    const el = /** @type {HTMLElement | undefined} */ (this.refs['status']);
    if (!el) return;
    // textContent, never innerHTML — this string is ours today, but the habit
    // is what keeps it safe when someone later interpolates a server value.
    el.textContent = message;
    el.dataset.tone = tone;
  }
}

if (!customElements.get('invite-code-form')) {
  customElements.define('invite-code-form', InviteCodeForm);
}
